import { AppHypotheekMixin } from '@/views/hypotheken/mixins/AppHypotheekMixin';
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { authService } from '@/services/AuthService';
import { User } from 'oidc-client';
import { Action, Mutation } from 'vuex-class';
import { Hypotheek } from '@/model/Hypotheek';
import { RawLocation } from 'vue-router';

@Component({
  components: {}
})
export default class AppMenu extends Mixins(AppHypotheekMixin) {
  public isHypotheekExpanded = false;
  public isUserExpanded = false;
  public isMobileExpanded = false;
  public isNavbarFixed = false;
  public isCustomer = false;
  public menuStyle: any = {};
  private resizeCallback: any;
  private scrollCallback: any;
  private keydownCallback: any;

  @Action('logout')
  private logout!: () => void;
  @Mutation('hypotheken/selectCurrentHypotheeknummer')
  private selectCurrentHypotheeknummer!: (hypotheeknummer: string) => {};
  @Mutation('sessionSuspended/storeLastUrl')
  private storeLastUrl!: (lastUrl: string) => void;
  private user: User | null = null;

  created() {
    authService.getUser().then(user => {
      this.user = user;
    });
    authService.isCustomer().then(isCustomer => {
      this.isCustomer = isCustomer;
    });
  }

  mounted() {
    this.resizeCallback = this.onResize.bind(this);
    this.scrollCallback = this.onScroll.bind(this);
    this.keydownCallback = this.onKeydown.bind(this);
    window.addEventListener('resize', this.resizeCallback);
    window.addEventListener('scroll', this.scrollCallback);
    window.addEventListener('keydown', this.keydownCallback);
    if (!this.hypotheek && this.hypotheken.length) {
      this.isHypotheekExpanded = true;
      this.isMobileExpanded = true;
    }
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCallback);
    window.removeEventListener('scroll', this.scrollCallback);
    window.removeEventListener('keydown', this.keydownCallback);
    document.body.style.overflow = '';
  }

  handleNavbarChange(id: string) {
    console.log('current active id is ' + id);
  }

  public get canAflossen(): boolean {
    return !!this.hypotheek?.canAflossen;
  }

  public onResize() {
    this.closeMenu();
  }

  public skipToMain(): void {
    try {
      const el = document.getElementById('main') as HTMLElement | null;
      if (!el) return;
      const hadTabindex = el.hasAttribute('tabindex');
      if (!hadTabindex) {
        el.setAttribute('tabindex', '-1');
      }
      el.focus();
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (!hadTabindex) {
        el.removeAttribute('tabindex');
      }
    } catch (e) {
      // silent fail
    }
  }

  public onScroll() {
    const el = this.$refs.topHeader as HTMLElement;
    this.isNavbarFixed = window.scrollY > el.offsetHeight;
  }

  public get hasMenu(): boolean {
    return this.$route.meta.menu !== false;
  }

  public get brandingLogoTo(): RawLocation | undefined {
    return this.hasMenu && this.$route.name !== 'hypotheek' ? { name: 'hypotheek' } : undefined;
  }

  get needOverlay(): boolean {
    return !(this.canHypotheek || this.hypotheken.length === 0);
  }

  get isMenuExpanded(): boolean {
    return this.isHypotheekExpanded || this.isUserExpanded || this.isMobileExpanded;
  }

  get authenticated(): boolean {
    return !!this.user && !this.user.expired;
  }

  get canHypotheek(): boolean {
    return !!this.hypotheek;
  }

  get canBerichten(): boolean {
    return this.canHypotheek;
  }

  get hasBouwdepot(): boolean {
    return !!(this.hypotheek && this.hypotheek.bouwdepot);
  }

  get canBouwdepot(): boolean {
    return this.canHypotheek && this.hasBouwdepot;
  }

  get canWijzigingen(): boolean {
    return this.canHypotheek;
  }

  get canWoningwaardeWijzigen(): boolean {
    return !!this.hypotheek && this.hypotheek.canWoningwaardeWijzigen;
  }

  get canGebruikerGegevens(): boolean {
    return this.canHypotheek || this.hypotheken.length === 0;
  }

  public isCurrentHypotheek(hypotheek: Hypotheek): boolean {
    return this.hypotheeknummer === hypotheek.id;
  }

  public selectHypotheek(hypotheek: Hypotheek): void {
    const mustNav = (!this.hypotheek || this.hypotheek.id !== hypotheek.id) && this.$route.name !== 'hypotheek';
    const mustClose = !this.hypotheek || this.hypotheek.id === hypotheek.id;
    this.selectCurrentHypotheeknummer(hypotheek.id);
    if (mustNav) {
      this.$router.push({ name: 'hypotheek' });
    }
    if (mustClose) {
      this.closeMenu();
    }
  }

  closeMenu(force = false): void {
    this.isHypotheekExpanded = false;
    this.isUserExpanded = false;
    this.isMobileExpanded = false;
  }

  @Watch('isMenuExpanded')
  watchMenuExpanded(open: boolean) {
    if (open) {
      document.body.style.overflow = 'hidden';
      this.$nextTick(() => {
        const menuEl = (this.$el as HTMLElement).querySelector('.nav-collapse-menu') as HTMLElement | null;
        if (menuEl) {
          menuEl.focus?.();
        }
      });
    } else {
      document.body.style.overflow = '';
    }
  }

  onKeydown(ev: KeyboardEvent) {
    const key = (ev as any).key || (ev as any).keyIdentifier;
    const keyCode = (ev as any).keyCode || (ev as any).which;
    if (key === 'Escape' || key === 'Esc' || keyCode === 27) {
      this.closeMenu();
    }
  }

  stopPropagation(ev: Event) {
    ev.stopPropagation();
    ev.preventDefault();
    return false;
  }

  logOut(): void {
    this.logout();
    this.storeLastUrl(this.$route.fullPath);
    authService.logout();
  }

  @Watch('isNavbarFixed')
  watchFixedMenu() {
    if (this.isNavbarFixed) {
      const n = (this.$refs.navbar as any).$el as HTMLElement;
      this.menuStyle = {
        top: `${n.offsetHeight}px`,
        'max-height': `calc(100% - ${n.offsetHeight}px)`
      };
    } else {
      this.menuStyle = {};
    }
  }
}
